import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Tooltip, Divider, message } from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import { MailLogItem, TabProps } from "../../../types/dataControl";

const MailLogsTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  const [mailLogs, setMailLogs] = useState<MailLogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 获取邮件日志列表
  const fetchMailLogs = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; logs: MailLogItem[] };
      }>(`${API_BASE_URL}/api/v3/admin/mail/logs`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setMailLogs(response.data.data.logs);
        setTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取邮件日志失败");
      }
    } catch (error) {
      console.error("获取邮件日志失败:", error);
      message.error("获取邮件日志失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, pagination]);

  // 初始加载数据
  useEffect(() => {
    fetchMailLogs();
  }, [fetchMailLogs]);

  // 处理表格变化
  const handleTableChange: TableProps<MailLogItem>["onChange"] = (
    pagination
  ) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  // 定义表格列
  const columns: TableProps<MailLogItem>["columns"] = [
    {
      title: "收件人",
      dataIndex: "to",
      key: "to",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (email) => (
        <Tooltip title={email}>
          <span>{email}</span>
        </Tooltip>
      ),
    },
    {
      title: "主题",
      dataIndex: "subject",
      key: "subject",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (subject) => (
        <Tooltip title={subject}>
          <span>{subject}</span>
        </Tooltip>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status ? "success" : "error"}>
          {status ? "发送成功" : "发送失败"}
        </Tag>
      ),
    },
    {
      title: "附件",
      dataIndex: "has_attach",
      key: "has_attach",
      width: 100,
      render: (hasAttach) => (
        <Tag color={hasAttach ? "blue" : "default"}>
          {hasAttach ? "有附件" : "无附件"}
        </Tag>
      ),
    },
    {
      title: "用户ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 120,
      render: (userId) => <span>{userId === 0 ? "-" : userId}</span>,
    },
    {
      title: "发送时间",
      dataIndex: "time",
      key: "time",
      width: 170,
      render: (time) => new Date(time).toLocaleString("zh-CN"),
    },
    {
      title: "错误信息",
      dataIndex: "error",
      key: "error",
      ellipsis: {
        showTitle: false,
      },
      render: (error) => {
        if (!error) return "-";
        return (
          <Tooltip title={error}>
            <span style={{ color: "#ff4d4f" }}>{error}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Divider style={{ margin: "12px 0 24px" }} />

      {/* 邮件日志列表表格 */}
      <Table
        columns={columns}
        dataSource={mailLogs}
        rowKey={(record) => `${record.to}-${record.time}`}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />
    </>
  );
};

export default MailLogsTab;
